import type { Era, EraId } from '@/models/types';
import { ERAS, INITIAL_RESOURCES } from '@/models/constants';
import { BuildingManager } from '@/systems/BuildingManager';
import { ResourceManager } from '@/systems/ResourceManager';

export class PrestigeManager {
  private currentEraIndex: number = 0;
  private prestigeCount: number = 0;
  private prestigeMultiplier: number = 1;

  getCurrentEra(): Era {
    return ERAS[this.currentEraIndex]!;
  }

  getNextEra(): Era | null {
    if (this.currentEraIndex >= ERAS.length - 1) return null;
    return ERAS[this.currentEraIndex + 1]!;
  }

  getPrestigeCount(): number {
    return this.prestigeCount;
  }

  getPrestigeMultiplier(): number {
    return this.prestigeMultiplier;
  }

  canPrestige(buildings: BuildingManager): boolean {
    const totalRate = buildings.getTotalProductionRate();
    return totalRate >= this.getCurrentEra().productionThreshold;
  }

  getProgressToPrestige(buildings: BuildingManager): number {
    const totalRate = buildings.getTotalProductionRate();
    const threshold = this.getCurrentEra().productionThreshold;
    if (threshold <= 0) return 1;
    return Math.min(1, totalRate / threshold);
  }

  prestige(resources: ResourceManager, buildings: BuildingManager): number {
    if (!this.canPrestige(buildings)) return 0;

    // Calculate shards BEFORE reset
    const totalRate = buildings.getTotalProductionRate();
    const shardsEarned = Math.floor(Math.sqrt(totalRate / 100));

    // Apply prestige multiplier from current era
    this.prestigeMultiplier *= this.getCurrentEra().prestigeMultiplier;

    // Advance era (wrap to 0 for infinite ascension post-MVP)
    if (this.currentEraIndex < ERAS.length - 1) {
      this.currentEraIndex++;
    }

    // Reset resources and buildings
    resources.setAll({ ...INITIAL_RESOURCES });
    buildings.setLevels({});

    this.prestigeCount++;
    return shardsEarned;
  }

  restoreState(eraId: EraId, prestigeCount: number, prestigeMultiplier: number): void {
    const index = ERAS.findIndex(e => e.id === eraId);
    this.currentEraIndex = index >= 0 ? index : 0;
    this.prestigeCount = prestigeCount;
    this.prestigeMultiplier = prestigeMultiplier;
  }
}
